import React, {
  useContext,
  useState
} from 'react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import {
  ELSDropDown,
} from '@els/els-component-form-field-react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import cx from 'classnames';
import { useHistory } from 'react-router';
import {
  ActivityTypeMetricsDto,
  QuizSessionDto,
  RecommendationDto,
  RemediationActivityTypeDto,
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { AssessmentGoalDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { SELECT_OPTION } from '../../utilities/app.utilities';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import IconWithText from '../icon-with-text/IconWithText.component';
import { TotalsStatus } from './TotalsStatus.component';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayoutModifier } from '../flex/flex.constants';
import RemediationCompleteBox from './RemediationCompleteBox.component';
import { RoutePath } from '../app/app.constants';
import {
  postRaaSQuizSessionAction,
  trackAdobeAction,
  trackNewRelicAction
} from '../app/app-state-actions.utilities';
import { AppContext } from '../app/AppProvider.component';
import {
  getQuizAttemptFromBaseState,
  getQuizTotals,
  handleNavigateToContent,
  handleNavigateToContentPerformance,
  isGoalComplete
} from '../../utilities/remediation.utilities';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import {
  getQuestionNumberOptions
} from './remediation-quiz.utilities';
import { camelCase, flowRight } from 'lodash';

type RemediationQuizPropsOnly = {
  assessmentGoal: AssessmentGoalDto;
  recommendation: RecommendationDto;
  metricCounts: ActivityTypeMetricsDto;
  showReviewQuizButton: boolean;
  continueQuizzing: boolean;
  showBorder: boolean;
}

export type RemediationQuizProps = RemediationQuizPropsOnly
  & ELSPropsFromModalService
  & ELSPropsFromToastService;

export type RemediationQuizState = {
  selectedQuizOption: string | number;
}

const RemediationQuizComponent = (props: RemediationQuizProps) => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const baseState = appState.remediationBaseState;
  const history = useHistory();

  if (!props.metricCounts) {
    return null;
  }

  const [state, setState] = useState<RemediationQuizState>({
    selectedQuizOption: SELECT_OPTION.value,
  });

  const setIsLaunching = (isLaunchingRecommendation: boolean) => {
    setAppState((prev) => ({ ...prev, isLaunchingRecommendation }));
  };

  const handleSelectQuizOption = (selectedOption: string | number) => {
    setState({
      ...state,
      selectedQuizOption: selectedOption
    });
  };

  const handleViewPerformance = () => {
    const recommendationAttempt = getQuizAttemptFromBaseState(baseState);

    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.QUIZ_RESULTS_VIEWED,
        props: {
          recommendationId: props.recommendation?.id,
          recommendationAttemptId: recommendationAttempt?.id,
          remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
          assessmentId: baseState.eolsAssessment?.id
        }
      }
    });

    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:reviewQuiz`,
        }
      }
    });

    handleNavigateToContentPerformance({
      appState,
      appStateRef,
      setAppState,
      recommendation: props.recommendation,
      recommendationAttempt,
      assessment: baseState?.eolsAssessment,
      quizSession: null,
      history,
      ref: RoutePath.REMEDIATION_STUDENT_HOME
    });
  };

  const handleStartQuiz = () => {
    const recommendationAttempt = getQuizAttemptFromBaseState(baseState);
    if (!recommendationAttempt) {
      return;
    }

    let sessionQuestionCount = state.selectedQuizOption as number;

    const options = getQuestionNumberOptions(props.metricCounts) || [];
    const option = options.find((item) => item.value === sessionQuestionCount);
    const optionDisplay = option ? camelCase(option.name) : '';

    if (sessionQuestionCount === SELECT_OPTION.value) {
      props.toastService.openToast({
        ...BASE_TOAST_CONFIG,
        component: (
          <div>
            <p>Please select number of questions.</p>
          </div>
        ),
        type: ELSWithToastService.types.NEGATIVE
      });
      return;
    }

    if (sessionQuestionCount === -1) {
      const recTotals = getQuizTotals(baseState);
      sessionQuestionCount = recTotals.activityTypeTotalGoalCompleted >= recTotals.activityTypeAttemptGoal
        ? recTotals.activityTypeAttemptGoal
        : recTotals.activityTypeAttemptGoal - recTotals.activityTypeTotalGoalCompleted;
    }

    // Track the Start Quiz button click with the number of questions selected
    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:startQuiz:${optionDisplay}`,
          content: [{
            details: `plp:quizQuestions=${sessionQuestionCount}`,
            type: 'plp:quiz'
          }]
        }
      }
    });

    setIsLaunching(true);

    postRaaSQuizSessionAction({
      appState,
      appStateRef,
      setAppState,
      quizSessionDto: {
        recommendationAttemptId: recommendationAttempt.id,
        totalQuestions: sessionQuestionCount
      }
    }).then((quizSession: QuizSessionDto) => {
      trackNewRelicAction({
        appStateRef,
        actionProps: {
          action: NewRelicAnalyticsAction.QUIZ_SESSION_STARTED,
          props: {
            recommendationId: props.recommendation.id,
            recommendationAttemptId: recommendationAttempt.id,
            quizSessionId: quizSession.id,
            totalQuestions: sessionQuestionCount,
            remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
            assessmentId: baseState.eolsAssessment?.id
          }
        }
      });

      handleNavigateToContent({
        appState,
        appStateRef,
        setAppState,
        recommendationAttempt,
        recommendation: props.recommendation,
        assessment: baseState?.eolsAssessment,
        quizSession,
        history,
        ref: RoutePath.REMEDIATION_STUDENT_HOME,
      });
    }).catch(() => setIsLaunching(false));
  };

  const { metricCounts } = props;

  const isComplete = isGoalComplete(metricCounts);

  return (
    <div className={cx('c-ssa-remediation-quiz-modal', {
      'c-ssa-remediation-quiz-modal--border': props.showBorder
    })}>

      {
        isComplete && (
          <RemediationCompleteBox activityType={RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE} />
        )
      }

      <div>
        {
          props.continueQuizzing && (
            <div className="c-ssa-remediation-goal-modal__message">
              <strong>Assess</strong> your understanding by answering <strong>quiz questions:</strong>
            </div>
          )

        }
        <div className={props.continueQuizzing && 'c-ssa-remediation-goal-modal__content'}>
          <table className="c-ssa-remediation-goal-modal__goal-table">
            <tbody>
              {
                !props.continueQuizzing && (
                  <tr>
                    <td>
                      <IconWithText iconName="syllabus" iconPrefix="hmds">Instructions</IconWithText>
                    </td>
                    <td>You must attempt all <strong>{metricCounts.activityTypeAttemptGoal}</strong> questions and answer at least <strong>{metricCounts.activityTypeTotalGoal}</strong> correctly.</td>
                  </tr>
                )
              }
              <tr>
                <td>
                  <IconWithText iconName="statistics" iconPrefix="gizmo">Questions Attempted</IconWithText>
                </td>
                <td>
                  <TotalsStatus
                    completedGoal={metricCounts.activityTypeAttemptGoal}
                    completed={metricCounts.activityTypeAttemptGoalCompleted}
                    total={metricCounts.activityTypeAttemptGoal}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <IconWithText iconName="statistics" iconPrefix="gizmo">Questions Correct</IconWithText>
                </td>
                <td>
                  <TotalsStatus
                    completedGoal={metricCounts.activityTypeTotalGoal}
                    completed={metricCounts.activityTypeTotalGoalCompleted}
                    total={metricCounts.activityTypeAttemptGoal}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div className="o-els-container c-els-divider u-els-color-n0" />

          <div className="o-els-container o-els-container--1x1o2">

            <ELSDropDown
              name="select-quiz-questions"
              options={getQuestionNumberOptions(props.metricCounts)}
              changeHandler={(e, value: string) => handleSelectQuizOption(value)}
              value={state.selectedQuizOption}
            >
              I&apos;d like to complete:
            </ELSDropDown>
          </div>

          <div className="o-els-container">
            <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.GUTTERS]}>
              <FlexItem>
                <button
                  type="button"
                  className="c-els-button c-els-button--primary c-els-button--small"
                  onClick={handleStartQuiz}
                >
                  Start quiz
                </button>
              </FlexItem>
              <FlexItem isRender={props.showReviewQuizButton && isComplete}>
                <button
                  type="button"
                  className="c-els-button c-els-button--secondary c-els-button--small"
                  onClick={handleViewPerformance}
                >
                  Review quiz
                </button>
              </FlexItem>
            </FlexLayout>

          </div>
        </div>
      </div>
    </div>
  );
};

const enhance = flowRight(
  ELSWithModalService,
  ELSWithToastService,
);

const RemediationQuiz = enhance(RemediationQuizComponent as unknown as React.FunctionComponent<unknown>) as unknown as React.FC<RemediationQuizPropsOnly>;

export default RemediationQuiz;
